import clsx from 'clsx'
import { differenceInDays } from 'date-fns'
import { useAtomValue } from 'jotai'
import { SlidersHorizontal } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import { aFiltersFind, aUser } from '~/atoms'
import { ControlTooltip } from '~/components/common'
import { Listing, getLeaseMetaValues, getMetaLabelValues } from '~/models'
import { cn, fmtNumberAbbr } from '~/utils'

export default function ListingMeta({
  className,
  listing,
  isOffMarket = false,
}: {
  className?: string
  listing: Listing
  isOffMarket?: boolean
}) {
  const user = useAtomValue(aUser)
  const filters = useAtomValue(aFiltersFind)

  const filtersString = useMemo(() => {
    return JSON.stringify(filters).toLowerCase()
  }, [filters])

  const renderLeaseMeta = () => {
    const values = getLeaseMetaValues(listing)
    if (values.length === 0) {
      return null
    }
    return (
      <>
        {values.map(({ label, value }, index) => {
          return (
            <React.Fragment key={index}>
              <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
                {label}
              </dt>
              <dd className="col-span-7 py-0.5 text-right font-semibold">
                {value}
              </dd>
            </React.Fragment>
          )
        })}
      </>
    )
  }

  const renderTransactionDateField = useCallback(() => {
    const created = new Date(listing.activeDate || listing.created)
    if (
      listing.status === 'sold' &&
      listing.transactions &&
      listing.transactions.length > 0 &&
      listing.transactions[0].sale
    ) {
      const dom = differenceInDays(
        new Date(listing.transactions[0].date),
        created
      )
      return (
        <>
          <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
            Sold Date
          </dt>
          <dd className="col-span-7 py-0.5 text-right font-semibold">
            {listing.transactions[0].date.split('T')[0]}
          </dd>

          {dom > 10 && (
            <>
              <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
                Days on Market
              </dt>
              <dd className="col-span-7 py-0.5 text-right font-semibold">
                {dom}
              </dd>
            </>
          )}
        </>
      )
    } else if (listing.offMarketDate) {
      const dom = differenceInDays(new Date(listing.offMarketDate), created)
      return (
        <>
          <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
            {listing.type === 'sold' ? 'Transaction Date' : 'Off Market Date'}
          </dt>
          <dd className="col-span-7 py-0.5 text-right font-semibold">
            {listing.offMarketDate.split('T')[0]}
          </dd>
          {listing.status === 'sold' && dom > 10 && (
            <>
              <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
                Days on Market
              </dt>
              <dd className="col-span-7 py-0.5 text-right font-semibold">
                {dom}
              </dd>
            </>
          )}
        </>
      )
    }
    return null
  }, [listing])

  const renderSaleTransactionFields = useCallback(() => {
    const soldPrice =
      listing.status === 'sold' &&
      listing.transactions &&
      listing.transactions.length > 0 &&
      listing.transactions[0].confidential !== true &&
      listing.transactions[0].sale?.price

    if (!soldPrice) {
      return null
    }
    const soldAcres =
      listing.propertyTypes[0] == 'Land' && listing.transactions![0].sale!.acres
    const soldBuildingSf =
      (listing.propertyTypes[0] != 'Land' &&
        listing.transactions![0].sale!.buildingSf) ??
      listing.meta.buildingSf
    return (
      <>
        <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
          Sold Price
        </dt>
        <dd className="col-span-7 py-0.5 text-right font-semibold">
          {!!soldAcres || !!soldBuildingSf ? (
            <ControlTooltip
              side="right"
              text={
                soldAcres
                  ? `$${fmtNumberAbbr(soldPrice / soldAcres)}/ac`
                  : soldBuildingSf
                    ? `$${fmtNumberAbbr(soldPrice / soldBuildingSf)}/SF`
                    : ''
              }>
              <span>${soldPrice.toLocaleString()}</span>
            </ControlTooltip>
          ) : (
            `$${soldPrice.toLocaleString()}`
          )}
        </dd>
      </>
    )
  }, [listing])

  return (
    <dl className={clsx(className, 'grid grid-cols-12 text-base')}>
      {renderTransactionDateField()}
      {renderSaleTransactionFields()}
      {!isOffMarket && (
        <>
          {renderLeaseMeta()}
          {getMetaLabelValues(listing).map(({ key, label, value }, index) => {
            return (
              <React.Fragment key={index}>
                <dt
                  className={cn(
                    'col-span-5 inline-flex items-center py-0.5 font-medium text-muted-foreground',
                    filtersString.includes(
                      key
                        .replace('filters', 'meta')
                        .replace('min', '')
                        .replace('max', '')
                        .toLowerCase()
                    ) && 'font-semibold text-foreground'
                  )}>
                  {filtersString.includes(
                    key
                      .replace('filters', 'meta')
                      .replace('min', '')
                      .replace('max', '')
                      .toLowerCase()
                  ) && <SlidersHorizontal className="mr-1 h-4 w-4" />}
                  {label}
                </dt>
                <dd className="col-span-7 py-0.5 text-right font-semibold">
                  {value}
                </dd>
              </React.Fragment>
            )
          })}
          {/* {renderCommission()} */}
          <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
            County
          </dt>
          <dd className="col-span-7 py-0.5 text-right font-semibold">
            {listing.property.county}
          </dd>
        </>
      )}
      {listing.availableDate && (
        <>
          <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
            Available
          </dt>
          <dd className="col-span-7 py-0.5 text-right font-semibold">
            {listing.availableDate?.split('T')[0] ?? ''}
          </dd>
        </>
      )}
      <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
        Created
      </dt>
      <dd className="col-span-7 py-0.5 text-right font-semibold">
        {listing.created.split('T')[0]}
      </dd>
      <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
        Updated
      </dt>
      <dd className="col-span-7 py-0.5 text-right font-semibold">
        {listing.modified.split('T')[0]}
      </dd>
      <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
        Listing ID
      </dt>
      <dd className="col-span-7 py-0.5 text-right font-semibold">
        {listing.tid}
      </dd>
      {listing.property.parcelApn && (
        <>
          <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
            Parcel APN
          </dt>
          <dd className="col-span-7 py-0.5 text-right font-semibold">
            {listing.property.parcelApn}
          </dd>
        </>
      )}
      {listing.property.additionalParcelApns &&
        listing.property.additionalParcelApns.length > 0 && (
          <>
            <dt className="col-span-5 py-0.5 font-medium text-muted-foreground">
              Additional Parcel APNs
            </dt>
            <dd className="col-span-7 py-0.5 text-right font-semibold">
              {listing.property.additionalParcelApns.map((parcel) => (
                <p key={parcel}>{parcel}</p>
              ))}
            </dd>
          </>
        )}
    </dl>
  )
}
